import { useState } from "react";
import { Repeat, ChevronDown, Check, Settings2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import {
  WEEKDAY_NAMES,
  recurrenceLabel,
  recurrenceColor,
  RECURRENCE_COLORS,
} from "../lib/recurrence";
import CustomRecurrenceBuilder from "./CustomRecurrenceBuilder";

const PRESETS = [
  { id: "off",       label: "Off",             rule: null },
  { id: "daily",     label: "Daily",           rule: { freq: "daily", interval: 1 } },
  { id: "weekdays",  label: "Weekdays",        rule: { freq: "weekdays", interval: 1 } },
  { id: "weekly",    label: "Weekly",          rule: { freq: "weekly", interval: 1 } },
  { id: "monthly",   label: "Monthly",         rule: { freq: "monthly", interval: 1, monthly_mode: "day_of_month" } },
  { id: "quarterly", label: "Quarterly",       rule: { freq: "quarterly", interval: 1 } },
  { id: "yearly",    label: "Yearly",          rule: { freq: "yearly", interval: 1 } },
];

function presetIdFor(rule) {
  if (!rule || !rule.freq) return "off";
  if ((rule.interval || 1) === 1) {
    if (rule.freq === "weekly" && (rule.by_weekdays?.length ?? 0) === 0) return "weekly";
    if (rule.freq === "monthly" && (rule.monthly_mode || "day_of_month") === "day_of_month") return "monthly";
    if (["daily", "weekdays", "quarterly", "yearly"].includes(rule.freq)) return rule.freq;
    if (rule.freq === "weekly" && (rule.by_weekdays?.length ?? 0) > 0) return "weekly";
    if (rule.freq === "monthly" && rule.monthly_mode === "weekday_of_month") return "monthly";
  }
  return "custom";
}

export default function RepeatPicker({ value, onChange, accentColor = "#FFB224" }) {
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const presetId = presetIdFor(value);
  const isCustom = presetId === "custom";
  const label = value ? recurrenceLabel(value) : "Does not repeat";
  const tagColor = recurrenceColor(value) || "currentColor";

  const select = (preset) => {
    onChange(preset.rule);
    setOpen(false);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="repeat-picker-trigger"
            className={cn(
              "w-full flex items-center justify-between gap-2 mt-1.5 h-10 px-3 rounded-md border border-input bg-background text-sm font-medium transition-colors",
              "hover:bg-secondary/50 focus:outline-none focus:ring-1 focus:ring-primary"
            )}
          >
            <span className="flex items-center gap-2 min-w-0">
              <Repeat
                size={14}
                strokeWidth={2}
                style={value ? { color: tagColor } : {}}
                className={value ? "" : "text-muted-foreground"}
              />
              <span className="truncate">{label}</span>
              {isCustom && (
                <span
                  className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                  style={{
                    backgroundColor: `${RECURRENCE_COLORS.custom}1A`,
                    color: RECURRENCE_COLORS.custom,
                  }}
                >
                  Custom
                </span>
              )}
            </span>
            <ChevronDown size={14} className="text-muted-foreground shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          data-testid="repeat-picker-popover"
          className="w-72 p-2"
          align="start"
        >
          <div className="space-y-0.5">
            {PRESETS.map((p) => {
              const color = p.id === "off" ? "#94a3b8" : RECURRENCE_COLORS[p.id] || accentColor;
              const active = presetId === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`repeat-preset-${p.id}`}
                  onClick={() => select(p)}
                  className={cn(
                    "w-full text-left px-2.5 py-2 rounded-md flex items-center justify-between text-sm transition-colors",
                    active
                      ? "bg-secondary text-foreground"
                      : "text-foreground hover:bg-secondary/60"
                  )}
                >
                  <span className="flex items-center gap-2.5">
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    {p.label}
                  </span>
                  {active && <Check size={14} className="text-muted-foreground" />}
                </button>
              );
            })}
            <div className="h-px bg-border my-1.5" />
            <button
              type="button"
              data-testid="repeat-preset-custom"
              onClick={() => {
                setOpen(false);
                setCustomOpen(true);
              }}
              className={cn(
                "w-full text-left px-2.5 py-2 rounded-md flex items-center justify-between text-sm transition-colors",
                isCustom
                  ? "bg-secondary text-foreground"
                  : "text-foreground hover:bg-secondary/60"
              )}
            >
              <span className="flex items-center gap-2.5">
                <Settings2 size={13} style={{ color: RECURRENCE_COLORS.custom }} />
                Custom…
              </span>
              {isCustom && <Check size={14} className="text-muted-foreground" />}
            </button>
          </div>

          {/* Inline weekly day selector when "Weekly" is active */}
          {presetId === "weekly" && (
            <div className="mt-3 border-t border-border pt-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2 px-1">
                Repeat on
              </div>
              <div className="flex flex-wrap gap-1.5 px-0.5">
                {WEEKDAY_NAMES.map((d, idx) => {
                  const selected = (value?.by_weekdays || []).includes(idx);
                  return (
                    <button
                      key={d}
                      type="button"
                      data-testid={`repeat-weekly-${idx}`}
                      onClick={() => {
                        const current = value?.by_weekdays || [];
                        const next = selected
                          ? current.filter((x) => x !== idx)
                          : [...current, idx].sort();
                        onChange({ ...(value || { freq: "weekly", interval: 1 }), by_weekdays: next });
                      }}
                      style={
                        selected
                          ? {
                              backgroundColor: RECURRENCE_COLORS.weekly,
                              color: "#fff",
                              borderColor: RECURRENCE_COLORS.weekly,
                            }
                          : {}
                      }
                      className={cn(
                        "w-9 h-9 rounded-md border border-border text-xs font-medium transition-colors",
                        !selected && "hover:bg-secondary/60 text-foreground"
                      )}
                    >
                      {d.charAt(0)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>

      <CustomRecurrenceBuilder
        open={customOpen}
        onClose={() => setCustomOpen(false)}
        value={value}
        onSave={(rule) => {
          onChange(rule);
          setCustomOpen(false);
        }}
      />
    </>
  );
}
